import {
	Button,
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Input,
	Label,
	Separator,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@monbits/ui";
import { ChevronRight, Coins, Loader2, X } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import settingsQueries from "src/queries/settings-queries";
import type { CurrencySetting } from "src/types";

const ManageCurrencySettingsPage: React.FC = () => {
	const [selectedSetting, setSelectedSetting] =
		React.useState<CurrencySetting | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);

	const [minWithdrawalUsd, setMinWithdrawalUsd] = React.useState<string>("");
	const [minInternalTransferUsd, setMinInternalTransferUsd] =
		React.useState<string>("");
	const [serviceFeeFixedUsd, setServiceFeeFixedUsd] =
		React.useState<string>("");
	const [withdrawalServiceFeeUsd, setWithdrawalServiceFeeUsd] =
		React.useState<string>("");
	const [isEnabled, setIsEnabled] = React.useState<boolean>(false);
	const [isDeprecated, setIsDeprecated] = React.useState<boolean>(false);

	const {
		data: settings = [],
		isLoading,
		refetch,
	} = settingsQueries.listCurrencySettings(false);

	const updateMutation = settingsQueries.updateCurrencySetting();

	const handleRowClick = (setting: CurrencySetting) => {
		setSelectedSetting(setting);
		setMinWithdrawalUsd(setting.minWithdrawalUsd.toString());
		setMinInternalTransferUsd(setting.minInternalTransferUsd.toString());
		setServiceFeeFixedUsd(setting.serviceFeeFixedUsd.toString());
		setWithdrawalServiceFeeUsd(setting.withdrawalServiceFeeUsd.toString());
		setIsEnabled(setting.isEnabled);
		setIsDeprecated(setting.isDeprecated);
		setIsDrawerOpen(true);
	};

	const handleSave = async () => {
		if (!selectedSetting) return;

		try {
			setIsSaving(true);

			const data = {
				minWithdrawalUsd: minWithdrawalUsd
					? Number.parseFloat(minWithdrawalUsd)
					: null,
				minInternalTransferUsd: minInternalTransferUsd
					? Number.parseFloat(minInternalTransferUsd)
					: null,
				serviceFeeFixedUsd: serviceFeeFixedUsd
					? Number.parseFloat(serviceFeeFixedUsd)
					: null,
				withdrawalServiceFeeUsd: withdrawalServiceFeeUsd
					? Number.parseFloat(withdrawalServiceFeeUsd)
					: null,
				isEnabled,
				isDeprecated,
			};

			await updateMutation.mutateAsync({
				ticker: selectedSetting.ticker,
				data,
			});

			toast.success("Currency setting updated successfully");
			setIsDrawerOpen(false);
			refetch();
		} catch (error) {
			console.error("Failed to update currency setting:", error);
			toast.error("Failed to update currency setting");
		} finally {
			setIsSaving(false);
		}
	};

	const formatDate = (dateStr: string): string => {
		const date = new Date(dateStr);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getCurrencyTypeBadge = (type: string) => {
		const typeMap: Record<
			string,
			"success" | "warning" | "error" | "info" | "default"
		> = {
			coin: "info",
			token: "default",
			fiat: "default",
		};
		return (
			<StatusBadge type={typeMap[type]}>
				{type.charAt(0).toUpperCase() + type.slice(1)}
			</StatusBadge>
		);
	};

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">Currency Settings</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Manage per-currency configuration and fees
					</p>
				</div>
			</div>

			{isLoading ? (
				<div className="flex justify-center items-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			) : settings.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 px-8 text-center border rounded-lg bg-muted/50">
					<Coins className="h-12 w-12 text-muted-foreground mb-4" />
					<h3 className="text-lg font-semibold">No currency settings</h3>
					<p className="text-sm text-muted-foreground mt-2">
						No currency settings have been configured yet
					</p>
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[140px]">Currency</TableHead>
							<TableHead className="w-[120px]">Blockchain</TableHead>
							<TableHead className="w-[130px]">Min Withdrawal</TableHead>
							<TableHead className="w-[140px]">Withdrawal Fee</TableHead>
							<TableHead className="w-[140px]">Min Transfer</TableHead>
							<TableHead className="w-[100px]">Status</TableHead>
							<TableHead className="w-12" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{settings.map((setting) => (
							<TableRow
								key={setting.ticker}
								onClick={() => handleRowClick(setting)}
								className="cursor-pointer hover:bg-muted/50"
							>
								<TableCell>
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<CryptoIcon
												ticker={setting.ticker}
												className="h-5 w-5"
												showComposite={true}
											/>
											<span className="font-mono font-semibold">
												{setting.ticker}
											</span>
										</div>
										<span className="text-xs text-muted-foreground">
											{setting.displayName}
										</span>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex flex-col gap-1">
										<span className="font-medium">{setting.blockchain}</span>
										<span className="text-xs text-muted-foreground">
											{setting.blockchainName}
										</span>
									</div>
								</TableCell>
								<TableCell className="font-medium">
									${setting.minWithdrawalUsd.toFixed(2)}
								</TableCell>
								<TableCell className="font-medium">
									${setting.withdrawalServiceFeeUsd.toFixed(2)}
								</TableCell>
								<TableCell className="font-medium">
									${setting.minInternalTransferUsd.toFixed(2)}
								</TableCell>
								<TableCell>
									{setting.isDeprecated ? (
										<StatusBadge type="error">Deprecated</StatusBadge>
									) : setting.isEnabled ? (
										<StatusBadge type="success">Enabled</StatusBadge>
									) : (
										<StatusBadge type="default">Disabled</StatusBadge>
									)}
								</TableCell>
								<TableCell>
									<ChevronRight className="h-4 w-4 text-muted-foreground" />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<Drawer
				open={isDrawerOpen}
				onOpenChange={(open) => {
					setIsDrawerOpen(open);
					if (!open) {
						setTimeout(() => setSelectedSetting(null), 300);
					}
				}}
				direction="right"
			>
				<DrawerContent className="h-screen overflow-hidden flex flex-col w-full max-w-full sm:max-w-sm md:max-w-[600px]">
					<div className="flex flex-col h-full w-full">
						<DrawerHeader className="flex-shrink-0 relative">
							<DrawerTitle className="text-lg font-medium pr-8">
								Edit Currency Setting
							</DrawerTitle>
							{selectedSetting && (
								<DrawerDescription className="text-sm text-muted-foreground font-mono font-semibold">
									{selectedSetting.ticker}
								</DrawerDescription>
							)}
							<DrawerClose asChild>
								<Button
									variant="ghost"
									size="icon"
									className="absolute right-4 top-4 h-8 w-8 rounded-full"
								>
									<X className="h-4 w-4" />
									<span className="sr-only">Close</span>
								</Button>
							</DrawerClose>
						</DrawerHeader>

						<Separator />

						<div className="flex-1 overflow-y-auto">
							{selectedSetting && (
								<div className="px-6 pb-6 space-y-6 mt-6">
									<div className="space-y-4">
										<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
											Details
										</h3>
										<div className="space-y-3">
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Ticker
												</div>
												<div className="flex items-center gap-2">
													<CryptoIcon
														ticker={selectedSetting.ticker}
														className="h-6 w-6"
														showComposite={true}
													/>
													<span className="font-mono text-sm font-semibold">
														{selectedSetting.ticker}
													</span>
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Display Name
												</div>
												<div className="text-sm">
													{selectedSetting.displayName}
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Blockchain
												</div>
												<div className="text-sm">
													{selectedSetting.blockchain} -{" "}
													{selectedSetting.blockchainName}
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Currency Type
												</div>
												<div className="text-sm">
													{getCurrencyTypeBadge(selectedSetting.currencyType)}
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Decimals
												</div>
												<div className="text-sm font-mono">
													{selectedSetting.decimals}
												</div>
											</div>
											<div>
												<div className="text-xs font-medium text-muted-foreground mb-1">
													Last Updated
												</div>
												<div className="text-sm">
													{formatDate(selectedSetting.updatedAt)}
												</div>
											</div>
										</div>
									</div>

									<Separator />

									<div className="space-y-4">
										<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
											Configuration
										</h3>
										<div className="space-y-4">
											<div className="space-y-2">
												<Label htmlFor="min-withdrawal">
													Minimum Withdrawal (USD)
												</Label>
												<Input
													id="min-withdrawal"
													type="number"
													step="0.01"
													min="0"
													value={minWithdrawalUsd}
													onChange={(e) => setMinWithdrawalUsd(e.target.value)}
												/>
												<p className="text-xs text-muted-foreground">
													Minimum amount in USD for withdrawals
												</p>
											</div>

											<div className="space-y-2">
												<Label htmlFor="min-internal-transfer">
													Minimum Internal Transfer (USD)
												</Label>
												<Input
													id="min-internal-transfer"
													type="number"
													step="0.01"
													min="0"
													value={minInternalTransferUsd}
													onChange={(e) =>
														setMinInternalTransferUsd(e.target.value)
													}
												/>
												<p className="text-xs text-muted-foreground">
													Minimum amount in USD for internal transfers
												</p>
											</div>

											<div className="space-y-2">
												<Label htmlFor="service-fee">
													Service Fee Fixed (USD)
												</Label>
												<Input
													id="service-fee"
													type="number"
													step="0.01"
													min="0"
													value={serviceFeeFixedUsd}
													onChange={(e) =>
														setServiceFeeFixedUsd(e.target.value)
													}
												/>
												<p className="text-xs text-muted-foreground">
													Fixed service fee in USD
												</p>
											</div>

											<div className="space-y-2">
												<Label htmlFor="withdrawal-service-fee">
													Withdrawal Service Fee (USD)
												</Label>
												<Input
													id="withdrawal-service-fee"
													type="number"
													step="0.01"
													min="0"
													value={withdrawalServiceFeeUsd}
													onChange={(e) =>
														setWithdrawalServiceFeeUsd(e.target.value)
													}
												/>
												<p className="text-xs text-muted-foreground">
													Withdrawal service fee in USD
												</p>
											</div>
										</div>
									</div>

									<Separator />

									<div className="space-y-4">
										<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
											Status
										</h3>
										<div className="space-y-4">
											<div className="flex items-center justify-between p-3 border rounded-lg">
												<div className="space-y-0.5">
													<Label htmlFor="is-enabled" className="text-sm">
														Enabled
													</Label>
													<div className="text-xs text-muted-foreground">
														Allow this currency for transactions
													</div>
												</div>
												<Switch
													id="is-enabled"
													checked={isEnabled}
													onCheckedChange={setIsEnabled}
												/>
											</div>

											<div className="flex items-center justify-between p-3 border rounded-lg">
												<div className="space-y-0.5">
													<Label htmlFor="is-deprecated" className="text-sm">
														Deprecated
													</Label>
													<div className="text-xs text-muted-foreground">
														Mark currency as deprecated
													</div>
												</div>
												<Switch
													id="is-deprecated"
													checked={isDeprecated}
													onCheckedChange={setIsDeprecated}
												/>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>

						<DrawerFooter className="flex-shrink-0">
							<div className="flex gap-3">
								<Button
									onClick={handleSave}
									disabled={isSaving}
									className="flex-1"
									size="lg"
								>
									{isSaving ? (
										<>
											<Loader2 className="h-4 w-4 mr-2 animate-spin" />
											Saving...
										</>
									) : (
										"Save Changes"
									)}
								</Button>
								<DrawerClose asChild>
									<Button
										variant="outline"
										className="flex-1"
										size="lg"
										disabled={isSaving}
									>
										Cancel
									</Button>
								</DrawerClose>
							</div>
						</DrawerFooter>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
};

export default ManageCurrencySettingsPage;
